import { Effect, Semaphore, type Scope } from 'effect';
import { attemptStudio, attemptStudioPromise, type StudioRuntimeError } from '../effect-runtime';
import {
  getPdfjsEffect,
  trackPdfjsDestroy,
  waitForPdfjsDestroysEffect,
  type PdfjsDocument,
} from './pdfjs';

export interface RenderPageOptions {
  /** Legacy cooperative check, observed between stages. Use signal for immediate cancellation. */
  cancelled?: () => boolean;
  signal?: AbortSignal;
}
export interface RenderTextLayerHandle {
  cancel: () => void;
}

// All documents share the configured PDF.js worker. A replacement must wait for
// the interrupted load's destroy before creating its own loading task.
const loading = Semaphore.makeUnsafe(1);
const textOwners = new WeakMap<HTMLDivElement, symbol>();
const textRendering = new WeakMap<HTMLDivElement, Semaphore.Semaphore>();

export const openPdfjsDocumentEffect = Effect.fn('studio.openPdfjsDocument')(
  function* (bytes: Uint8Array, adopt: (document: PdfjsDocument) => void) {
    yield* waitForPdfjsDestroysEffect();
    const pdfjs = yield* getPdfjsEffect();
    let adopted = false;
    const task = yield* Effect.acquireRelease(
      attemptStudio('pdfjs', 'open-document', () => {
        const task = pdfjs.getDocument({ data: bytes.slice() });
        void task.promise.catch(() => undefined);
        return task;
      }),
      (task) =>
        adopted
          ? Effect.void
          : attemptStudioPromise('pdfjs', 'document-destroy', () => {
              const destroy = task.destroy();
              trackPdfjsDestroy(destroy);
              return destroy;
            }).pipe(Effect.ignore),
    );
    const document = yield* attemptStudioPromise('pdfjs', 'open-document', () => task.promise);
    yield* attemptStudio('pdfjs', 'adopt-document', () => {
      adopt(document);
      adopted = true;
    });
  },
  Effect.scoped,
  loading.withPermit,
);

const checkCurrent = (stale: () => boolean) =>
  attemptStudio('pdfjs', 'render-current', () => {
    if (stale()) throw new Error('render cancelled');
  });

export const renderPageCanvasEffect = Effect.fn('studio.renderPageCanvas')(function* (
  pdfDoc: PdfjsDocument,
  pageNumber: number,
  scale: number,
  stale: () => boolean,
): Effect.fn.Return<HTMLCanvasElement, StudioRuntimeError, Scope.Scope> {
  yield* checkCurrent(stale);
  const page = yield* attemptStudioPromise('pdfjs', 'get-page', () => pdfDoc.getPage(pageNumber));
  yield* checkCurrent(stale);
  const prepared = yield* attemptStudio('pdfjs', 'page-canvas', () => {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D unavailable');
    return { canvas, context, viewport, outputScale };
  });
  let completed = false;
  const task = yield* Effect.acquireRelease(
    attemptStudio('pdfjs', 'render-page', () =>
      page.render({
        canvas: prepared.canvas,
        canvasContext: prepared.context,
        viewport: prepared.viewport,
        transform:
          prepared.outputScale !== 1
            ? [prepared.outputScale, 0, 0, prepared.outputScale, 0, 0]
            : undefined,
      }),
    ),
    (task) =>
      completed
        ? Effect.void
        : Effect.gen(function* () {
            yield* attemptStudio('pdfjs', 'render-cancel', () => task.cancel()).pipe(Effect.ignore);
            yield* attemptStudioPromise('pdfjs', 'render-settle', () => task.promise).pipe(
              Effect.ignore,
            );
            yield* attemptStudio('pdfjs', 'canvas-release', () => {
              prepared.canvas.width = 0;
              prepared.canvas.height = 0;
            }).pipe(Effect.ignore);
          }),
  );
  yield* attemptStudioPromise('pdfjs', 'render-page', () => task.promise);
  yield* checkCurrent(stale);
  completed = true;
  return prepared.canvas;
}, Effect.scoped);

const renderTextLayer = Effect.fn('studio.renderTextLayer')(function* (
  pdfDoc: PdfjsDocument,
  pageNumber: number,
  scale: number,
  container: HTMLDivElement,
  stale: () => boolean,
) {
  yield* checkCurrent(stale);
  const page = yield* attemptStudioPromise('pdfjs', 'get-page', () => pdfDoc.getPage(pageNumber));
  yield* checkCurrent(stale);
  const viewport = yield* attemptStudio('pdfjs', 'page-viewport', () =>
    page.getViewport({ scale }),
  );
  const textContent = yield* attemptStudioPromise('pdfjs', 'page-text', () =>
    page.getTextContent({ includeMarkedContent: true, disableNormalization: true }),
  );
  yield* checkCurrent(stale);
  const pdfjs = yield* getPdfjsEffect();
  // Keep PDF.js's original container/rotation contract. An old handle may
  // clear this container only while it still owns it.
  let adopted = false;
  let rendering: Promise<void> | undefined;
  const owner = Symbol();
  const clear = () => {
    if (textOwners.get(container) !== owner) return;
    textOwners.delete(container);
    container.replaceChildren();
  };
  yield* Effect.acquireRelease(
    attemptStudio('pdfjs', 'text-container', () => {
      container.replaceChildren();
      textOwners.set(container, owner);
    }),
    () =>
      adopted
        ? Effect.void
        : attemptStudio('pdfjs', 'text-container-release', clear).pipe(Effect.ignore),
  );
  const layer = yield* Effect.acquireRelease(
    attemptStudio(
      'pdfjs',
      'text-layer',
      () => new pdfjs.TextLayer({ textContentSource: textContent, container, viewport }),
    ),
    (layer) =>
      adopted
        ? Effect.void
        : Effect.gen(function* () {
            yield* attemptStudio('pdfjs', 'text-cancel', () => layer.cancel()).pipe(Effect.ignore);
            if (rendering)
              yield* attemptStudioPromise('pdfjs', 'text-settle', () => rendering!).pipe(
                Effect.ignore,
              );
          }),
  );
  rendering = yield* attemptStudio('pdfjs', 'render-text-layer', () => layer.render());
  yield* attemptStudioPromise('pdfjs', 'render-text-layer', () => rendering!);
  yield* checkCurrent(stale);
  adopted = true;
  let cancelled = false;
  return {
    cancel: () => {
      if (cancelled) return;
      cancelled = true;
      try {
        layer.cancel();
      } finally {
        clear();
      }
    },
  };
}, Effect.scoped);

export const renderTextLayerHandleEffect = Effect.fn('studio.renderTextLayerOwned')(function* (
  pdfDoc: PdfjsDocument,
  pageNumber: number,
  scale: number,
  container: HTMLDivElement,
  stale: () => boolean,
) {
  let semaphore = textRendering.get(container);
  if (!semaphore) {
    semaphore = Semaphore.makeUnsafe(1);
    textRendering.set(container, semaphore);
  }
  return yield* renderTextLayer(pdfDoc, pageNumber, scale, container, stale).pipe(
    semaphore.withPermit,
  );
});

export const getPageSizeEffect = Effect.fn('studio.getPageSize')(function* (
  pdfDoc: PdfjsDocument,
  pageNumber: number,
) {
  const page = yield* attemptStudioPromise('pdfjs', 'get-page', () => pdfDoc.getPage(pageNumber));
  return yield* attemptStudio('pdfjs', 'page-size', () => {
    const { width, height } = page.getViewport({ scale: 1 });
    return { width, height };
  });
});
